/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LOCALES,
} from '@airasia-rn/ui/localization';
import { getCookie, setCookie } from './cookies';
import { localeToPath } from './localePath';

// Re-export the locale/URL path helpers so existing `localeRedirect` importers
// keep working. They live in a dependency-free module (`./localePath`) so client
// code can import them without pulling in the i18n/localization chain.
export { localeToPath, pathToLocale } from './localePath';

/**
 * Resolves the locale from the `uhfLocale` cookie, falling back to DEFAULT_LANGUAGE
 * if the cookie value is missing or not a supported locale.
 */
export const resolveLocaleFromCookie = (cookieValue?: string): string => {
  const cookieLocale = cookieValue ?? getCookie('uhfLocale') ?? '';
  const supported = SUPPORTED_LOCALES as string[];
  return supported.includes(cookieLocale) ? cookieLocale : DEFAULT_LANGUAGE;
};

/**
 * Resolves the active locale: uses the path locale if supported,
 * otherwise falls back to the `uhfLocale` cookie via resolveLocaleFromCookie.
 *
 * Pass an explicit cookie value from server contexts. Omit the argument in
 * browser contexts to read from `document.cookie`.
 */
export const resolveLocale = (
  localeFromPath: string,
  cookieValue?: string,
): string => {
  const lowercasedLocaleFromPath = localeFromPath.toLowerCase();
  const supported = SUPPORTED_LOCALES as string[];
  if (supported.includes(lowercasedLocaleFromPath)) {
    return lowercasedLocaleFromPath;
  }
  return resolveLocaleFromCookie(cookieValue);
};

export type NextLocaleRedirectOptions = {
  /**
   * Use shallow URL update via `window.history.replaceState`.
   * Avoids triggering Next.js App Router layout teardown / server re-render
   * while keeping the browser URL and history truthful.
   */
  shallow?: boolean;
  /**
   * Optional client-side router navigation function (e.g. Next.js `router.replace`).
   * When provided, performs a client-side navigation without a full page reload.
   * If omitted, falls back to `window.location.href` full page reload.
   */
  replace?: (url: string) => void;
  /**
   * App basePath (e.g. '/v2') to strip before passing to `replace`, because Next.js
   * router automatically prepends basePath.
   */
  basePath?: string;
  /**
   * Optional callback invoked when a locale is selected.
   */
  onLocaleSelect?: (locale: string) => void;
};

/**
 * Creates a locale change handler for web apps that use `[locale]/[country]` in the
 * URL path. Replaces the first two-segment locale/country prefix (e.g., /en/gb/) with
 * the new locale. When `options.shallow` is true, updates the URL via `window.history.replaceState`.
 * When `options.replace` is provided, performs a client-side navigation via router;
 * otherwise falls back to a full page reload via `window.location.href`.
 * Handles apps served behind a base URL prefix (e.g., /v2/en/gb or /search/en/gb).
 */
export const createNextLocaleRedirect = (
  options?: NextLocaleRedirectOptions,
) => {
  return (locale: string) => {
    if (typeof window === 'undefined') return;

    setCookie('uhfLocale', locale, { days: 365 });

    try {
      window.dispatchEvent(
        new CustomEvent('uhfLocaleChange', { detail: locale }),
      );
    } catch {}

    options?.onLocaleSelect?.(locale);

    const { pathname, search, hash } = window.location;
    let newPath = pathname.replace(
      /\/[a-z]{2}\/[a-z]{2}(?=\/|$)/,
      `/${localeToPath(locale)}`,
    );

    if (newPath === pathname) {
      const base = options?.basePath ?? '';
      newPath = `${base}/${localeToPath(locale)}`;
    }

    const fullUrl = `${newPath}${search}${hash}`;

    if (options?.replace) {
      let appPath = newPath;
      if (options.basePath && appPath.startsWith(options.basePath)) {
        appPath = appPath.slice(options.basePath.length) || '/';
      }
      options.replace(`${appPath}${search}${hash}`);
    } else if (options?.shallow !== false) {
      window.history.replaceState(null, '', fullUrl);
    } else {
      window.location.href = fullUrl;
    }
  };
};
